import React, { createContext, useMemo } from 'react';
import type { AttackPathsData } from './markdown/AttackPathGraph';

export interface AttackPathLookupValue {
  getAttackPathsForFindingId: (findingId: string) => AttackPathsData | null;
}

const defaultLookup: AttackPathLookupValue = {
  getAttackPathsForFindingId: () => null,
};

export const AttackPathLookupContext =
  createContext<AttackPathLookupValue>(defaultLookup);

export function buildAttackPathLookupFromToolResults(
  content: Array<{ type: string; toolName?: string; content?: unknown }>,
): Map<string, AttackPathsData> {
  const map = new Map<string, AttackPathsData>();
  for (const block of content) {
    if (block.type !== 'toolResult' || block.toolName !== 'getAttackPathById') {
      continue;
    }
    let raw: unknown;
    if (typeof block.content === 'string') {
      try {
        raw = JSON.parse(block.content);
      } catch (err) {
        console.error(
          'buildAttackPathLookupFromToolResults: failed to parse toolResult content as JSON',
          {
            toolName: block.toolName,
            error: err,
          },
        );
        continue;
      }
    } else {
      raw = block.content;
    }
    const payload = raw as {
      data?: { id?: string; attackPaths?: AttackPathsData };
    };
    const data = payload?.data;
    if (!data || typeof data !== 'object') {
      continue;
    }
    const id = data.id;
    const ap = data.attackPaths;
    if (
      typeof id === 'string' &&
      id.trim() !== '' &&
      ap &&
      Array.isArray(ap.nodes)
    ) {
      map.set(id, { nodes: ap.nodes, edges: ap.edges ?? [] });
    }
  }
  return map;
}

interface AttackPathLookupProviderProps {
  lookup: Map<string, AttackPathsData>;
  children: React.ReactNode;
}

export const AttackPathLookupProvider: React.FC<
  AttackPathLookupProviderProps
> = ({ lookup, children }) => {
  const value = useMemo<AttackPathLookupValue>(
    () => ({
      getAttackPathsForFindingId: (findingId: string) =>
        lookup.get(findingId) ?? null,
    }),
    [lookup],
  );
  return (
    <AttackPathLookupContext.Provider value={value}>
      {children}
    </AttackPathLookupContext.Provider>
  );
};
